import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  type AdminDiscordConnection,
  type AdminPoll,
  type AdminPollChannels,
  type AdminPollCreateRequest,
  type AdminPollList,
  pollEmoji,
  pollLimits,
} from "@ternary/contracts"
import {
  Button,
  Checkbox,
  type Column,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Field,
  FormWizard,
  IconButton,
  Input,
  Menu,
  Muted,
  Select,
  Table,
  useToast,
  type WizardErrors,
  type WizardStep,
} from "@ternary/ui"
import { type ReactElement, useId, useState } from "react"
import { Navigate } from "react-router-dom"
import {
  AdminApiError,
  changePoll,
  downloadPollVotes,
  loadAdminDiscord,
  loadPollChannels,
  loadPolls,
  postPoll,
} from "../api.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { NavIcon } from "../components/nav-icons.js"

const DATE = new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" })
const COUNT = new Intl.NumberFormat("en-US")

/** How long a poll stays open: the choices Discord's own app offers. */
export const pollDurations = [
  { hours: 1, label: "1 hour" },
  { hours: 4, label: "4 hours" },
  { hours: 8, label: "8 hours" },
  { hours: 24, label: "1 day" },
  { hours: 72, label: "3 days" },
  { hours: 168, label: "1 week" },
  { hours: 336, label: "2 weeks" },
] as const

type Channel = AdminPollChannels["channels"][number]
type Guild = Pick<AdminDiscordConnection["guilds"][number], "id" | "name" | "reinstallUrl">

// Characters, as Discord counts them: an emoji is one.
const length = (value: string) => Array.from(value.trim()).length

// "A, B and C".
const listed = (names: readonly string[]) =>
  names.length < 2 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`

/**
 * What the bot needs in a channel before it can post a poll there: the Ternary role's bits when it
 * lacks them server-wide, else the channel's own permissions, which only an owner can change there.
 */
export function missingText(channel: Channel) {
  return channel.serverMissing.length > 0
    ? `Allow ${listed(channel.missing)} for the Ternary role in #${channel.name}.`
    : `Allow ${listed(channel.missing)} for the bot in #${channel.name}'s channel permissions.`
}

// The re-add link, asking for the bits the bot invite can add (View Channel and Send Messages are
// in every invite already).
function pollReinstallLink(reinstallUrl: string, missing: readonly string[]) {
  const extra = [
    missing.includes("Send Polls") ? "sendPolls" : null,
    missing.includes("Read Message History") ? "readMessageHistory" : null,
  ].filter(Boolean)
  if (missing.length === 0) return null
  return extra.length > 0
    ? `${reinstallUrl}${reinstallUrl.includes("?") ? "&" : "?"}extra=${extra.join(",")}`
    : reinstallUrl
}

interface Draft {
  allowMultiselect: boolean
  /** Each answer's text, and its optional emoji ("" for none). */
  answers: Array<{ emoji: string; text: string }>
  channelId: string
  durationHours: number
  question: string
}

const NEW_POLL: Draft = {
  allowMultiselect: false,
  answers: [
    { emoji: "", text: "" },
    { emoji: "", text: "" },
  ],
  channelId: "",
  durationHours: 24,
  question: "",
}

type Errors = WizardErrors

function questionErrors(draft: Draft, channel: Channel | undefined): Errors {
  const errors: Errors = {}
  if (!channel) errors.channelId = "Choose a channel."
  else if (channel.missing.length > 0) errors.channelId = missingText(channel)
  if (length(draft.question) === 0) errors.question = "Enter a question."
  else if (length(draft.question) > pollLimits.question) {
    errors.question = `Use up to ${pollLimits.question} characters.`
  }
  return errors
}

function answerErrors(draft: Draft): Errors {
  const errors: Errors = {}
  draft.answers.forEach(({ emoji, text }, index) => {
    if (length(text) === 0) errors[`answer-${index}`] = "Enter an answer, or remove it."
    else if (length(text) > pollLimits.answer) {
      errors[`answer-${index}`] = `Use up to ${pollLimits.answer} characters.`
    }
    if (emoji.trim() !== "" && pollEmoji(emoji.trim()) === null) {
      errors[`emoji-${index}`] = "Use one emoji, such as 🎉, or a server emoji like <:name:id>."
    }
  })
  return errors
}

/**
 * Post a poll, in two steps so neither scrolls: Question (the server and channel, the question,
 * how long it stays open, and whether members may pick more than one), then Answers (2 to 10). A
 * channel where the bot lacks a permission says which, with the re-add link when the invite can
 * add it. Every limit is Discord's, counted as Discord counts characters. The panel posts through
 * `onSubmit`; a refusal shows on Answers. `stepId` and `initialDraft` open a given step, as the
 * stories do.
 */
export function PostPollForm({
  channels,
  channelsError,
  guildId,
  guilds,
  initialDraft,
  onCancel,
  onGuildChange,
  onSubmit,
  stepId,
}: {
  /** The server's text channels; null while they load. */
  channels: AdminPollChannels["channels"] | null
  channelsError?: string | undefined
  guildId: string
  guilds: readonly Guild[]
  initialDraft?: Partial<Draft> | undefined
  onCancel(): void
  onGuildChange(guildId: string): void
  onSubmit(poll: AdminPollCreateRequest): Promise<unknown>
  stepId?: string | undefined
}): ReactElement {
  const formId = useId()
  const channelOf = (draft: Draft) => channels?.find(({ id }) => id === draft.channelId)
  const guild = guilds.find(({ id }) => id === guildId)
  const steps: WizardStep<Draft>[] = [
    {
      fields: ["channelId", "question", "durationHours", "allowMultiselect"],
      id: "question",
      render: ({ draft, errors, setDraft }) => {
        const channel = channelOf(draft)
        const reinstall =
          channel && guild?.reinstallUrl
            ? pollReinstallLink(guild.reinstallUrl, channel.serverMissing)
            : null
        return (
          <div className="admin-stack">
            {guilds.length > 1 ? (
              <Field label="Server">
                <Select
                  onChange={(event) => {
                    setDraft({ channelId: "" })
                    onGuildChange(event.target.value)
                  }}
                  value={guildId}
                >
                  {guilds.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name || option.id}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            <Field
              error={errors.channelId ?? channelsError}
              hint={
                channel && channel.missing.length > 0 ? (
                  <>
                    {missingText(channel)}{" "}
                    {reinstall ? <a href={reinstall}>Re-add the bot</a> : null}
                  </>
                ) : undefined
              }
              label="Channel"
              required
            >
              <Select
                disabled={channels === null}
                onChange={(event) => setDraft({ channelId: event.target.value })}
                value={draft.channelId}
              >
                <option value="">
                  {channels === null ? "Loading channels…" : "Choose a channel"}
                </option>
                {(channels ?? []).map((option) => (
                  <option key={option.id} value={option.id}>
                    #{option.name}
                    {option.missing.length > 0 ? " (needs permissions)" : ""}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              aside={<Muted>{`${length(draft.question)}/${pollLimits.question}`}</Muted>}
              error={errors.question}
              label="Question"
              required
            >
              <Input
                onChange={(event) => setDraft({ question: event.target.value })}
                value={draft.question}
              />
            </Field>
            <Field label="Open for">
              <Select
                onChange={(event) => setDraft({ durationHours: Number(event.target.value) })}
                value={String(draft.durationHours)}
              >
                {pollDurations.map(({ hours, label }) => (
                  <option key={hours} value={hours}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            <Checkbox
              checked={draft.allowMultiselect}
              label="Members may pick more than one answer"
              onChange={(event) => setDraft({ allowMultiselect: event.target.checked })}
            />
          </div>
        )
      },
      title: "Question",
      validate: (draft) => questionErrors(draft, channelOf(draft)),
    },
    {
      fields: ["answers"],
      id: "answers",
      render: ({ draft, errors, setDraft }) => {
        const full = draft.answers.length >= pollLimits.maxAnswers
        const setAnswers = (answers: Draft["answers"]) => setDraft({ answers })
        const setAnswer = (index: number, change: Partial<Draft["answers"][number]>) =>
          setAnswers(
            draft.answers.map((answer, at) => (at === index ? { ...answer, ...change } : answer)),
          )
        return (
          <fieldset className="admin-stack admin-poll-answers">
            <legend className="tny-visually-hidden">Answers</legend>
            <div className="admin-poll-answer-list">
              {draft.answers.map((answer, index) => {
                const textError = errors[`answer-${index}`]
                const emojiError = errors[`emoji-${index}`]
                const id = `${formId}-answer-${index}`
                return (
                  // Answers have no id until Discord gives them one: the place is the key.
                  // biome-ignore lint/suspicious/noArrayIndexKey: see above.
                  <div className="admin-poll-answer" key={index}>
                    <label className="tny-label admin-poll-answer-label" htmlFor={id}>
                      Answer {index + 1}
                    </label>
                    <Input
                      aria-describedby={emojiError ? `${id}-emoji-error` : undefined}
                      aria-invalid={emojiError ? true : undefined}
                      aria-label={`Emoji for answer ${index + 1}`}
                      className="admin-poll-emoji"
                      onChange={(event) => setAnswer(index, { emoji: event.target.value })}
                      placeholder="🙂"
                      value={answer.emoji}
                    />
                    <Input
                      aria-describedby={textError ? `${id}-error` : undefined}
                      aria-invalid={textError ? true : undefined}
                      id={id}
                      onChange={(event) => setAnswer(index, { text: event.target.value })}
                      suffix={`${length(answer.text)}/${pollLimits.answer}`}
                      value={answer.text}
                    />
                    {/* Each error its own message, so each field names the one about it. */}
                    {textError ? (
                      <p className="tny-error admin-poll-answer-error" id={`${id}-error`}>
                        {textError}
                      </p>
                    ) : null}
                    {emojiError ? (
                      <p className="tny-error admin-poll-answer-error" id={`${id}-emoji-error`}>
                        {emojiError}
                      </p>
                    ) : null}
                    <IconButton
                      disabled={draft.answers.length <= pollLimits.minAnswers}
                      icon={<NavIcon name="trash" />}
                      label={`Remove answer ${index + 1}`}
                      onClick={() => setAnswers(draft.answers.filter((_, at) => at !== index))}
                    />
                  </div>
                )
              })}
            </div>
            <div className="admin-row">
              <Button
                disabled={full}
                onClick={() => setAnswers([...draft.answers, { emoji: "", text: "" }])}
                size="sm"
                variant="secondary"
              >
                <NavIcon name="plus" />
                Add answer
              </Button>
              {full ? (
                <p className="admin-note">Discord allows up to {pollLimits.maxAnswers} answers.</p>
              ) : null}
            </div>
          </fieldset>
        )
      },
      title: "Answers",
      validate: answerErrors,
    },
  ]
  return (
    <FormWizard<Draft>
      headingLevel={2}
      initialDraft={{ ...NEW_POLL, ...initialDraft }}
      onCancel={onCancel}
      onSubmit={async (draft) => {
        await onSubmit({
          allowMultiselect: draft.allowMultiselect,
          answers: draft.answers.map(({ emoji, text }) => ({
            emoji: emoji.trim() === "" ? null : emoji.trim(),
            text: text.trim(),
          })),
          channelId: draft.channelId,
          durationHours: draft.durationHours,
          guildId,
          question: draft.question.trim(),
        })
      }}
      stepId={stepId}
      steps={steps}
      submitLabel="Post poll"
      title="Poll"
      titleHidden
    />
  )
}

function statusText(poll: AdminPoll) {
  if (poll.status === "open") return `Open until ${DATE.format(new Date(poll.expiresAt))}`
  const ended = DATE.format(new Date(poll.endedAt ?? poll.expiresAt))
  return poll.final ? `Ended ${ended}` : `Ended ${ended}, counting votes`
}

// The votes are an hourly snapshot until Discord finalizes them (poll-sync.ts): when they were
// read, so a new poll's zeros don't look like a fault. A final poll's votes are its results. Only
// an account with the actions has Refresh votes to point at.
function readText(poll: AdminPoll, writable: boolean) {
  if (poll.lastReadAt) return `Votes as of ${DATE.format(new Date(poll.lastReadAt))}`
  return writable
    ? "Votes not read yet. Refresh votes reads them now."
    : "Votes not read yet. They update every hour."
}

/**
 * The business's polls, newest first: the question with each answer's votes, the server, how many
 * members voted, and whether it is open. Owners and admins get each poll's actions: read its votes
 * again, end it now, and export one row per voter as CSV or Excel. It only shows.
 */
export function PollList({
  guilds,
  loading = false,
  onAction,
  polls,
  writable,
}: {
  guilds: readonly Guild[]
  loading?: boolean
  onAction(poll: AdminPoll, action: "refresh" | "end" | "csv" | "xlsx"): void
  polls: readonly AdminPoll[]
  writable: boolean
}): ReactElement {
  const names = new Map(guilds.map(({ id, name }) => [id, name || id]))
  const columns: Column<AdminPoll>[] = [
    {
      cell: (poll) => (
        <div className="admin-poll-question">
          <span>{poll.question}</span>
          <Muted>
            {poll.answers.map(({ text, votes }) => `${text} ${COUNT.format(votes)}`).join(" · ")}
          </Muted>
          {poll.final ? null : <Muted>{readText(poll, writable)}</Muted>}
        </div>
      ),
      header: "Question",
      key: "question",
    },
    {
      cell: (poll) => names.get(poll.guildId) ?? poll.guildId,
      header: "Server",
      key: "server",
      priority: 3,
    },
    {
      align: "end",
      cell: (poll) => COUNT.format(poll.voters),
      header: "Voters",
      key: "voters",
      priority: 2,
    },
    {
      cell: (poll) =>
        poll.noAccess ? (
          <div className="admin-poll-question">
            <span>{statusText(poll)}</span>
            <span className="tny-warning">
              The bot can no longer read this channel: re-add it or restore its permissions.
            </span>
          </div>
        ) : (
          statusText(poll)
        ),
      header: "Status",
      key: "status",
      priority: 2,
    },
  ]
  if (writable) {
    columns.push({
      align: "end",
      cell: (poll) => (
        <Menu
          icon={<NavIcon name="more" />}
          id={`poll-${poll.id}`}
          items={[
            {
              disabled: poll.final,
              label: "Refresh votes",
              onSelect: () => onAction(poll, "refresh"),
            },
            ...(poll.status === "open"
              ? [{ danger: true, label: "End poll now", onSelect: () => onAction(poll, "end") }]
              : []),
            { label: "Export CSV", onSelect: () => onAction(poll, "csv") },
            { label: "Export Excel (.xlsx)", onSelect: () => onAction(poll, "xlsx") },
          ]}
          label={`Actions for ${poll.question}`}
        />
      ),
      header: "Actions",
      key: "actions",
      skeleton: "action",
    })
  }
  return (
    <Table
      caption="Polls"
      captionHidden
      columns={columns}
      loading={loading}
      loadingLabel="Loading polls…"
      rowKey={(poll) => poll.id}
      rows={[...polls]}
    />
  )
}

const pollsKey = ["admin-polls"] as const

/**
 * Polls (Connections → Discord → Polls), while the deployment offers them: native Discord polls
 * the Ternary bot posts, each member's final answer kept, and a per-poll export with the voters'
 * customer details. Owners and admins post, end and export; anyone else sees the list.
 */
export function PollsPanel({ catalog }: { catalog: AdminDiscordConnection }): ReactElement {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [posting, setPosting] = useState(false)
  const [ending, setEnding] = useState<AdminPoll | null>(null)
  const [guildId, setGuildId] = useState(
    () => catalog.connection.serverId || catalog.guilds[0]?.id || "",
  )
  const polls = useInfiniteQuery({
    getNextPageParam: (page: AdminPollList) => page.nextCursor,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => loadPolls(pageParam, { signal }),
    queryKey: pollsKey,
  })
  const channels = useQuery({
    enabled: posting && guildId !== "",
    queryFn: ({ signal }) => loadPollChannels(guildId, { signal }),
    queryKey: ["poll-channels", guildId],
  })
  const failed = (error: Error) =>
    toast({
      message:
        error instanceof AdminApiError
          ? error.message
          : "Polls could not reach the server. Check your connection, then try again.",
      tone: "error",
    })
  const refreshList = () => queryClient.invalidateQueries({ queryKey: pollsKey })
  // A refusal, such as a channel the bot cannot post in, shows on the form's last step.
  const post = useMutation({
    mutationFn: async (request: AdminPollCreateRequest) => {
      try {
        return await postPoll(request)
      } catch (error) {
        throw error instanceof AdminApiError
          ? error
          : new Error("The poll could not be posted. Check your connection, then try again.")
      }
    },
    onSuccess: async (poll) => {
      setPosting(false)
      await refreshList()
      const channel = channels.data?.channels.find(({ id }) => id === poll.channelId)
      toast({
        message: channel ? `Poll posted in #${channel.name}.` : "Poll posted.",
        tone: "success",
      })
    },
  })
  const change = useMutation({
    mutationFn: ({ action, poll }: { action: "end" | "refresh"; poll: AdminPoll }) =>
      changePoll(poll.id, action),
    onError: failed,
    // Only an End closes its confirm: a refresh answering meanwhile leaves it open.
    onSettled: (_poll, _error, { action }) => {
      if (action === "end") setEnding(null)
    },
    onSuccess: async (poll, { action }) => {
      await refreshList()
      toast({
        message:
          action === "end"
            ? "Poll ended. Its final votes are kept once Discord counts them."
            : `Votes read: ${COUNT.format(poll.voters)} ${poll.voters === 1 ? "member" : "members"} voted.`,
        tone: "success",
      })
    },
  })
  const exportVotes = useMutation({
    mutationFn: ({ format, poll }: { format: "csv" | "xlsx"; poll: AdminPoll }) =>
      downloadPollVotes(poll.id, format),
    onError: failed,
    onSuccess: () => toast({ message: "Votes downloaded.", tone: "success" }),
  })

  const pages = polls.data?.pages ?? []
  const rows = pages.flatMap(({ data }) => data)
  const writable = pages[0]?.writable ?? false
  const postButton = <Button onClick={() => setPosting(true)}>Post a poll</Button>

  if (polls.isError) {
    return (
      <LoadError
        detail={errorDetail(polls.error)}
        message="We couldn't load your polls"
        onRetry={() => void polls.refetch()}
        retrying={polls.isFetching}
      />
    )
  }
  return (
    <div className="admin-stack">
      <div className="admin-row admin-poll-intro">
        <p className="admin-note">
          Native Discord polls the bot posts. Ternary reads their votes every hour and keeps each
          member's final answer.
        </p>
        {writable && rows.length > 0 ? postButton : null}
      </div>
      {!polls.isPending && rows.length === 0 ? (
        <EmptyState
          action={writable ? postButton : undefined}
          description="Ask your members a question in a channel, then export who answered what."
          title="Post your first poll"
        />
      ) : (
        <PollList
          guilds={catalog.guilds}
          loading={polls.isPending}
          onAction={(poll, action) => {
            if (action === "end") setEnding(poll)
            else if (action === "refresh") change.mutate({ action, poll })
            else exportVotes.mutate({ format: action, poll })
          }}
          polls={rows}
          writable={writable}
        />
      )}
      {polls.hasNextPage ? (
        <Button
          loading={polls.isFetchingNextPage}
          onClick={() => void polls.fetchNextPage()}
          variant="secondary"
        >
          Show older polls
        </Button>
      ) : null}
      <Dialog
        onClose={() => setPosting(false)}
        open={posting}
        pending={post.isPending}
        size="lg"
        title="Post a poll"
      >
        {posting ? (
          <PostPollForm
            channels={channels.data?.channels ?? (channels.isError ? [] : null)}
            channelsError={channels.isError ? errorDetail(channels.error) : undefined}
            guildId={guildId}
            guilds={catalog.guilds}
            onCancel={() => setPosting(false)}
            onGuildChange={setGuildId}
            onSubmit={(poll) => post.mutateAsync(poll)}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        body="Members can no longer vote. Ternary keeps each member's final answer once Discord counts the votes."
        confirmLabel="End poll"
        danger
        onCancel={() => setEnding(null)}
        onConfirm={() => {
          if (ending) change.mutate({ action: "end", poll: ending })
        }}
        open={ending !== null}
        pending={change.isPending && change.variables?.action === "end"}
        title={ending ? `End “${ending.question}” now?` : "End this poll now?"}
      />
    </div>
  )
}

/**
 * /dashboard/discord/polls: the Discord page's Polls tab while the deployment offers polls; with
 * the switch off it goes home, as any unknown address does.
 */
export function AdminPollsPage(): ReactElement | null {
  const query = useQuery({ queryFn: loadAdminDiscord, queryKey: ["admin-discord"] })
  if (query.isPending) return null
  // Only a loaded answer without polls sends the link home; a failed load says so.
  if (query.isError) {
    return (
      <LoadError
        detail={errorDetail(query.error)}
        message="We couldn't load your Discord settings"
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    )
  }
  return (
    <Navigate replace to={query.data.community?.polls ? "/dashboard/discord?tab=polls" : "/"} />
  )
}
